import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  Save,
  Trash2,
  ExternalLink,
  Search,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Package,
  Layers,
  Image as ImageIcon,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import * as adminApi from "../../api/adminApi";
import { resolveImageUrl } from "../../utils/images";
import "./CollectionEdit.css";

export default function CollectionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [collection, setCollection] = useState(null);

  // Products
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");

  // Form
  const [form, setForm] = useState({
    name: "",
    arabicName: "",
    description: "",
    arabicDescription: "",
    isEnabled: true,
    displayOrder: 0,
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [collData, prodData] = await Promise.all([
        adminApi.fetchAdminCollection(id),
        adminApi.fetchAdminProducts(),
      ]);

      setCollection(collData);
      setForm({
        name: collData.name || "",
        arabicName: collData.arabicName || "",
        description: collData.description || "",
        arabicDescription: collData.arabicDescription || "",
        isEnabled: collData.isEnabled !== false,
        displayOrder: collData.displayOrder ?? 0,
      });
      setSelectedProductIds((collData.products || []).map((p) => p.id));
      setAllProducts(Array.isArray(prodData) ? prodData : prodData?.items || []);
    } catch (e) {
      addToast(e.message || "Failed to load collection data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // Filtered products list by search query
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q))
    );
  }, [allProducts, productSearch]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      addToast("Collection name is required.", "error");
      return;
    }

    setSaving(true);
    try {
      await adminApi.updateAdminCollection(id, {
        name: form.name.trim(),
        arabicName: form.arabicName.trim() || null,
        description: form.description.trim() || null,
        arabicDescription: form.arabicDescription.trim() || null,
        isEnabled: form.isEnabled,
        displayOrder: Number(form.displayOrder),
        collectionProducts: selectedProductIds,
      });
      await loadData();
      addToast("Collection updated successfully.", "success");
    } catch (err) {
      addToast(err.message || "Failed to update collection.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const updated = await adminApi.uploadCollectionImage(id, file);
      addToast("Banner image updated successfully.", "success");
      const cacheBust = `${updated?.imageUrl?.split("?")[0] || ""}?t=${Date.now()}`;
      setCollection((prev) => ({ ...prev, imageUrl: cacheBust }));
    } catch (err) {
      addToast(err.message || "Failed to upload image.", "error");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((item) => item !== productId)
        : [...prev, productId]
    );
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredProducts.map((p) => p.id);
    setSelectedProductIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const deselectAllFiltered = () => {
    const filteredIdSet = new Set(filteredProducts.map((p) => p.id));
    setSelectedProductIds((prev) => prev.filter((id) => !filteredIdSet.has(id)));
  };

  const handleDeleteCollection = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteAdminCollection(id);
      addToast(`Collection "${collection?.name || "Collection"}" deleted.`, "success");
      setShowDeleteModal(false);
      navigate("/admin/collections");
    } catch (err) {
      addToast(err.message || "Failed to delete collection.", "error");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <span className="page-loading__dot" />
      </div>
    );
  }

  if (!collection && !loading) {
    return (
      <div className="collection-edit-not-found">
        <button
          className="btn-outline"
          onClick={() => navigate("/admin/collections")}
        >
          <ArrowLeft size={16} /> Back to collections
        </button>
        <p>Collection not found.</p>
      </div>
    );
  }

  return (
    <div className="collection-edit">
      {/* Top Breadcrumb Header */}
      <div className="collection-edit__head">
        <div className="collection-edit__nav">
          <button
            type="button"
            className="btn-ghost collection-edit__back"
            onClick={() => navigate("/admin/collections")}
          >
            <ArrowLeft size={16} /> Collections
          </button>
          <span className="collection-edit__divider">/</span>
          <span className="collection-edit__crumb-current">{collection.name}</span>
        </div>

        <div className="collection-edit__head-actions">
          <Link
            to={`/collections/${collection.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline collection-edit__store-btn"
          >
            <ExternalLink size={15} /> View in Store
          </Link>
          <button
            type="button"
            className="btn-primary collection-edit__save-top"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="spin-icon" /> Saving…
              </>
            ) : (
              <>
                <Save size={16} /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="collection-edit__title-row">
        <div className="collection-edit__title-wrap">
          <h2 className="collection-edit__title">{collection.name}</h2>
          {collection.arabicName && (
            <span className="collection-edit__ar-badge" dir="rtl" lang="ar">
              {collection.arabicName}
            </span>
          )}
        </div>
        <span
          className={`status-badge ${
            form.isEnabled ? "status-active" : "status-inactive"
          }`}
        >
          <span className="status-badge__dot" />
          {form.isEnabled ? "Active" : "Inactive"}
        </span>
      </div>

      {/* Main Grid */}
      <div className="collection-edit__grid">
        {/* Left Column: Form & Products */}
        <div className="collection-edit__main">
          {/* Section 1: General & Localization Details */}
          <section className="admin-section">
            <div className="admin-section__header">
              <h3 className="admin-section__title">Collection Information</h3>
              <p className="admin-section__subtitle">
                Configure both English and Arabic titles and descriptions for multi-language display.
              </p>
            </div>

            <form onSubmit={handleSave} className="collection-form">
              {/* Titles Row */}
              <div className="collection-form__row">
                <div className="auth-field">
                  <label htmlFor="edit-col-name-en">
                    Collection Title (English) <span className="required-star">*</span>
                  </label>
                  <input
                    id="edit-col-name-en"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Silk Robes"
                    required
                    maxLength={100}
                    disabled={saving}
                  />
                </div>

                <div className="auth-field">
                  <div className="auth-field__label-wrap">
                    <label htmlFor="edit-col-name-ar">Collection Title (Arabic)</label>
                    <span className="optional-tag">(Optional)</span>
                  </div>
                  <input
                    id="edit-col-name-ar"
                    type="text"
                    dir="rtl"
                    value={form.arabicName}
                    onChange={(e) => setForm({ ...form, arabicName: e.target.value })}
                    placeholder="مثال: أرواب الحرير الفاخرة"
                    maxLength={100}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Descriptions Row */}
              <div className="collection-form__row">
                <div className="auth-field">
                  <div className="auth-field__label-wrap">
                    <label htmlFor="edit-col-desc-en">Description (English)</label>
                    <span className="char-count">{form.description.length} / 1000</span>
                  </div>
                  <textarea
                    id="edit-col-desc-en"
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Provide a compelling description of this collection..."
                    maxLength={1000}
                    disabled={saving}
                  />
                </div>

                <div className="auth-field">
                  <div className="auth-field__label-wrap">
                    <label htmlFor="edit-col-desc-ar">Description (Arabic)</label>
                    <span className="char-count">{form.arabicDescription.length} / 1000</span>
                  </div>
                  <textarea
                    id="edit-col-desc-ar"
                    rows={4}
                    dir="rtl"
                    value={form.arabicDescription}
                    onChange={(e) => setForm({ ...form, arabicDescription: e.target.value })}
                    placeholder="وصف شيق وموجز للمجموعة يظهر للعملاء..."
                    maxLength={1000}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Display & Status Row */}
              <div className="collection-form__row collection-form__row--settings">
                <div className="auth-field">
                  <label htmlFor="edit-col-order">
                    Display Order Index
                  </label>
                  <input
                    id="edit-col-order"
                    type="number"
                    min={0}
                    max={255}
                    value={form.displayOrder}
                    onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                    disabled={saving}
                  />
                  <small className="field-hint">
                    Lower numbers appear earlier in storefront navigation.
                  </small>
                </div>

                <div className="collection-toggle-field">
                  <label className="collection-toggle-label">
                    <input
                      type="checkbox"
                      className="collection-toggle-input"
                      checked={form.isEnabled}
                      onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                      disabled={saving}
                    />
                    <span className="collection-toggle-slider" />
                    <div className="collection-toggle-text">
                      <strong>Published & Active</strong>
                      <span>Make this collection visible across storefront navigation.</span>
                    </div>
                  </label>
                </div>
              </div>
            </form>
          </section>

          {/* Section 2: Products Selection */}
          <section className="admin-section">
            <div className="admin-section__header">
              <div className="admin-section__title-row">
                <div>
                  <h3 className="admin-section__title">Assigned Products</h3>
                  <p className="admin-section__subtitle">
                    Select products included in this collection ({selectedProductIds.length} selected).
                  </p>
                </div>

                <div className="collection-products__actions">
                  <button
                    type="button"
                    className="btn-ghost btn-xs"
                    onClick={selectAllFiltered}
                    disabled={saving}
                  >
                    Select All ({filteredProducts.length})
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-xs"
                    onClick={deselectAllFiltered}
                    disabled={saving}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
            </div>

            {/* Product search box */}
            <div className="collection-products__search-bar">
              <Search size={15} className="collection-products__search-icon" />
              <input
                type="text"
                placeholder="Filter products by title..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="collection-products__search-input"
              />
              {productSearch && (
                <button
                  type="button"
                  className="collection-products__search-clear"
                  onClick={() => setProductSearch("")}
                  aria-label="Clear product filter"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Products scroll list */}
            <div className="collection-products-list">
              {filteredProducts.map((p) => {
                const isSelected = selectedProductIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className={`collection-product-item ${isSelected ? "selected" : ""}`}
                    onClick={() => toggleProduct(p.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggleProduct(p.id);
                      }
                    }}
                  >
                    <div className="collection-product-item__check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        aria-label={`Select ${p.name}`}
                      />
                    </div>

                    <div className="collection-product-item__image-wrap">
                      {p.thumbnailImage ? (
                        <img
                          src={resolveImageUrl(p.thumbnailImage)}
                          alt=""
                          className="collection-product-item__thumb"
                        />
                      ) : (
                        <div className="collection-product-item__thumb-placeholder">
                          <ImageIcon size={14} />
                        </div>
                      )}
                    </div>

                    <div className="collection-product-info">
                      <strong className="collection-product-name">{p.name}</strong>
                      {p.sku && <span className="collection-product-sku">SKU: {p.sku}</span>}
                    </div>

                    {isSelected && (
                      <span className="collection-product-selected-tag">
                        <Check size={12} /> Included
                      </span>
                    )}
                  </div>
                );
              })}

              {filteredProducts.length === 0 && (
                <div className="collection-products-empty">
                  <Package size={24} />
                  <p>No products match "{productSearch}".</p>
                </div>
              )}
            </div>

            <div className="admin-section__actions" style={{ marginTop: "20px" }}>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="spin-icon" /> Saving…
                  </>
                ) : (
                  <>
                    <Save size={16} /> Save Collection & Products
                  </>
                )}
              </button>
            </div>
          </section>

          {/* Section 3: Danger Zone */}
          <section className="admin-section admin-section--danger">
            <div className="admin-danger-zone">
              <div className="admin-danger-zone__text">
                <h4>Delete this Collection</h4>
                <p>
                  Permanently delete this collection and its banner asset. Associated products will remain intact in your store catalog.
                </p>
              </div>
              <button
                type="button"
                className="btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 size={15} /> Delete Collection
              </button>
            </div>
          </section>
        </div>

        {/* Right Column: Hero Image & Overview */}
        <div className="collection-edit__side">
          {/* Image Upload Card */}
          <section className="admin-section">
            <h3 className="admin-section__title">Collection Hero Image</h3>
            <p className="admin-section__subtitle">
              High-resolution cover displayed at the top of the collection storefront.
            </p>

            <div className="collection-image-box">
              {collection?.imageUrl ? (
                <div className="collection-image-preview-wrap">
                  <img
                    src={resolveImageUrl(collection.imageUrl)}
                    alt={collection.name}
                    className="collection-image-preview"
                  />
                </div>
              ) : (
                <div className="collection-image-placeholder">
                  <ImageIcon size={36} />
                  <span>No hero image uploaded</span>
                </div>
              )}
            </div>

            <label className="btn-outline collection-upload-btn">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                style={{ display: "none" }}
              />
              {uploading ? (
                <>
                  <Loader2 size={16} className="spin-icon" /> Uploading…
                </>
              ) : (
                <>
                  <Upload size={16} />
                  {collection?.imageUrl ? "Replace Hero Image" : "Upload Hero Image"}
                </>
              )}
            </label>
            <p className="upload-tip">Recommended aspect ratio 16:9 or 21:9 (WebP, JPG, PNG)</p>
          </section>

          {/* Quick Stats Card */}
          <section className="admin-section collection-meta-card">
            <h3 className="admin-section__title">Summary</h3>
            <div className="collection-meta-list">
              <div className="collection-meta-item">
                <span className="collection-meta-label">Products Assigned</span>
                <span className="collection-meta-value">{selectedProductIds.length} items</span>
              </div>
              <div className="collection-meta-item">
                <span className="collection-meta-label">Order Index</span>
                <span className="collection-meta-value">#{form.displayOrder}</span>
              </div>
              <div className="collection-meta-item">
                <span className="collection-meta-label">Visibility</span>
                <span className="collection-meta-value">
                  {form.isEnabled ? "Public" : "Draft / Hidden"}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="admin-modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) setShowDeleteModal(false);
          }}
        >
          <div className="admin-modal admin-modal--danger">
            <div className="admin-modal__header">
              <div className="admin-modal__header-icon admin-modal__header-icon--danger">
                <AlertTriangle size={22} />
              </div>
              <div className="admin-modal__header-text">
                <h3 id="delete-dialog-title">Delete Collection</h3>
                <p className="admin-modal__hint">This action cannot be undone.</p>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => !deleting && setShowDeleteModal(false)}
                aria-label="Cancel deletion"
              >
                <X size={18} />
              </button>
            </div>

            <div className="admin-modal__delete-body">
              <p>
                Are you sure you want to permanently delete{" "}
                <strong>"{collection.name}"</strong>?
              </p>
              <div className="admin-modal__danger-notice">
                <ul>
                  <li>
                    <strong>Products:</strong> The <strong>{selectedProductIds.length}</strong>{" "}
                    assigned products will remain in your catalog, but will no longer belong to
                    this collection.
                  </li>
                  <li>
                    <strong>Hero Image:</strong> The image asset for this collection will be
                    deleted.
                  </li>
                </ul>
              </div>
            </div>

            <div className="admin-modal__actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteCollection}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} className="spin-icon" /> Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 size={15} /> Delete Collection
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
